import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Store, User, LogOut, Download, HelpCircle, Heart, BookOpen } from 'lucide-react';
import { useWorkOS } from '../../features/workos/context/WorkOSContext';
import logoImage from '../../assets/ContextUI blackbackground.svg';
import DonationModal from '../../features/donations/components/DonationModal';
import { logger } from '../../utils/logger';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);
  const location = useLocation();
  const { logout, user, isAuthenticated, login, getValidAccessToken } = useWorkOS();

  logger.log('WorkOS Auth state:', { isAuthenticated, user });

  // Fetch unread feedback count for authenticated users
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!isAuthenticated) {
        setUnreadFeedbackCount(0);
        return;
      }

      try {
        const accessToken = await getValidAccessToken();
        const response = await fetch('/.netlify/functions/marketplace-my-workflows?action=unread_count', {
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setUnreadFeedbackCount(data.unread_count || 0);
        }
      } catch (err) {
        logger.error('Failed to fetch unread feedback count:', err);
      }
    };

    fetchUnreadCount();
    // Refresh every 5 minutes
    const interval = setInterval(fetchUnreadCount, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, getValidAccessToken]);

  const isActiveLink = (path) => location.pathname === path;

  const handleLogin = async () => {
    logger.log('Login button clicked');
    login(location.pathname);
  };

  const handleLogout = async () => {
    try {
      logger.log('Logout button clicked');
      await logout();
      // DON'T redirect here - the logout function handles the redirect to WorkOS
    } catch (error) {
      logger.error('Logout error:', error);
    }
  };

  return (
    <header className="fixed w-full bg-black border-b border-gray-800 z-50">
      <nav className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0 pl-6">
            <Link to="/" className="flex items-center space-x-5">
              <img 
                src={logoImage} 
                alt="ContextUI" 
                className="h-10 w-auto" 
                onError={(e) => {
                  logger.error('Logo failed to load');
                  e.target.style.display = 'none';
                }}
              />
            </Link>
          </div>
          
          <div className="hidden md:block pr-6">
            <div className="flex items-center space-x-10">
              
              <Link
                to="/exchange"
                className={`relative flex items-center space-x-2 transition-colors duration-300 text-sm tracking-widest uppercase font-medium ${
                  isActiveLink('/exchange') ? 'text-white font-bold' : 'text-gray-300 hover:text-white'
                }`}
              >
                <Store className="h-4 w-4" />
                <span>Exchange</span>
                {unreadFeedbackCount > 0 && (
                  <span className="absolute -top-2 -right-3 px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                    {unreadFeedbackCount > 9 ? '9+' : unreadFeedbackCount}
                  </span>
                )}
              </Link>

              <Link
                to="/download"
                className={`flex items-center space-x-2 transition-colors duration-300 text-sm tracking-widest uppercase font-medium ${
                  isActiveLink('/download') ? 'text-white font-bold' : 'text-gray-300 hover:text-white'
                }`}
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </Link>

              <Link
                to="/docs"
                className={`flex items-center space-x-2 transition-colors duration-300 text-sm tracking-widest uppercase font-medium ${
                  isActiveLink('/docs') ? 'text-white font-bold' : 'text-gray-300 hover:text-white'
                }`}
              >
                <BookOpen className="h-4 w-4" />
                <span>Docs</span>
              </Link>

              <Link
                to="/support"
                className={`flex items-center space-x-2 transition-colors duration-300 text-sm tracking-widest uppercase font-medium ${
                  isActiveLink('/support') ? 'text-white font-bold' : 'text-gray-300 hover:text-white'
                }`}
              >
                <HelpCircle className="h-4 w-4" />
                <span>Support</span>
              </Link>

              <button
                onClick={() => setIsDonationModalOpen(true)}
                className="flex items-center space-x-2 transition-colors duration-300 text-sm tracking-widest uppercase font-medium text-red-400 hover:text-red-300"
              >
                <Heart className="h-4 w-4" />
                <span>Donate</span>
              </button>

              {!isAuthenticated ? (
                <button 
                  onClick={handleLogin}
                  className="ml-4 px-5 py-2 border-2 border-white text-white text-sm tracking-widest uppercase font-medium
                    transition-all duration-300 hover:bg-white hover:text-black"
                >
                  Sign In / Register
                </button>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/account"
                    className="flex items-center space-x-2 px-4 py-2 border border-white text-white text-sm tracking-widest uppercase font-medium
                      transition-all duration-300 hover:bg-white hover:text-black"
                  >
                    <User className="h-4 w-4" />
                    <span>Account</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 px-4 py-2 border border-white text-white text-sm tracking-widest uppercase font-medium
                      transition-all duration-300 hover:bg-white hover:text-black"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="md:hidden pr-6">
            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className="text-white hover:text-gray-300"
              aria-label="Toggle navigation menu"
              aria-expanded={isOpen}
            >
              <span className="sr-only">Open menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {isOpen && (
          <div className="md:hidden bg-black border-t border-gray-800">
            <div className="px-4 pt-2 pb-3 space-y-1">
              <Link
                to="/exchange"
                className="flex items-center space-x-2 text-gray-300 hover:text-white py-2"
                onClick={() => setIsOpen(false)}
              >
                <Store className="h-4 w-4" />
                <span>Exchange</span>
                {unreadFeedbackCount > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
                    {unreadFeedbackCount > 9 ? '9+' : unreadFeedbackCount}
                  </span>
                )}
              </Link>
              <Link
                to="/download"
                className="flex items-center space-x-2 text-gray-300 hover:text-white py-2"
                onClick={() => setIsOpen(false)}
              >
                <Download className="h-4 w-4" />
                <span>Download</span>
              </Link>
              <Link
                to="/docs"
                className="flex items-center space-x-2 text-gray-300 hover:text-white py-2"
                onClick={() => setIsOpen(false)}
              >
                <BookOpen className="h-4 w-4" />
                <span>Docs</span>
              </Link>
              <Link
                to="/support"
                className="flex items-center space-x-2 text-gray-300 hover:text-white py-2"
                onClick={() => setIsOpen(false)}
              >
                <HelpCircle className="h-4 w-4" />
                <span>Support</span>
              </Link>
              <button
                onClick={() => {
                  setIsDonationModalOpen(true);
                  setIsOpen(false);
                }}
                className="flex items-center space-x-2 text-red-400 hover:text-red-300 py-2 w-full text-left"
              >
                <Heart className="h-4 w-4" />
                <span>Donate</span>
              </button>
              {!isAuthenticated ? (
                <button 
                  onClick={handleLogin}
                  className="flex items-center space-x-2 text-gray-300 hover:text-white py-2 w-full text-left"
                >
                  <User className="h-4 w-4" />
                  <span>Sign In</span>
                </button>
              ) : (
                <>
                  <Link 
                    to="/account"
                    className="flex items-center space-x-2 text-gray-300 hover:text-white py-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <User className="h-4 w-4" />
                    <span>Account</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-2 text-gray-300 hover:text-white py-2 w-full text-left"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Sign Out</span>
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Donation Modal */}
      <DonationModal
        isOpen={isDonationModalOpen}
        onClose={() => setIsDonationModalOpen(false)}
      />
    </header>
  );
};

export default Navbar;